import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { questoesService, testesService, alternativasService } from '../services/database'
import EmptyState from '../components/shared/EmptyState'
import Breadcrumbs from '../components/shared/Breadcrumbs'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  QuestionMarkCircleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'

interface Alternativa {
  id: string
  questao_id: string
  texto: string
  correta: boolean
  created_at: string
}

interface Questao {
  id: string
  enunciado: string
  teste_id: string | null
  ordem: number
  tri_a: number | null
  tri_b: number | null
  tri_c: number | null
  created_by: string | null
  created_at: string
  updated_at: string
  alternativas: Alternativa[]
}

interface Teste {
  id: string
  title: string
}

const QuestoesPage = () => {
  const { profile } = useAuth()
  const [questoes, setQuestoes] = useState<Questao[]>([])
  const [testes, setTestes] = useState<Teste[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTeste, setSelectedTeste] = useState<string>('')
  const [showModal, setShowModal] = useState(false)
  const [editingQuestao, setEditingQuestao] = useState<Questao | null>(null)
  const [formData, setFormData] = useState({
    enunciado: '',
    teste_id: '',
    tri_a: '1',
    tri_b: '0',
    tri_c: '0.2',
    alternativas: [
      { texto: '', correta: false },
      { texto: '', correta: false },
      { texto: '', correta: false },
      { texto: '', correta: false }
    ]
  })

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (selectedTeste) {
      fetchQuestoesDoTeste(selectedTeste)
    } else {
      setQuestoes([])
    }
  }, [selectedTeste])

  const fetchData = async () => {
    try {
      setLoading(true)
      const testesData = await testesService.getAll()
      setTestes(testesData)

      if (testesData.length > 0) {
        setSelectedTeste(testesData[0].id)
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchQuestoesDoTeste = async (testeId: string) => {
    try {
      setLoading(true)
      const questoesData = await questoesService.getByTeste(testeId)

      const questoesCompletas = await Promise.all(
        questoesData.map(async (q) => {
          const alternativas = await alternativasService.getByQuestao(q.id)
          return {
            ...q,
            alternativas: alternativas || []
          } as Questao
        })
      )

      setQuestoes(questoesCompletas)
    } catch (error) {
      console.error('Erro ao carregar questões:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredQuestoes = questoes.filter(questao => {
    const matchesSearch = questao.enunciado.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesTeste = !selectedTeste || questao.teste_id === selectedTeste
    return matchesSearch && matchesTeste
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const hasCorrectAnswer = formData.alternativas.some(alt => alt.correta)
    if (!hasCorrectAnswer) {
      alert('Pelo menos uma alternativa deve estar marcada como correta!')
      return
    }

    const hasEmptyAlternative = formData.alternativas.some(alt => !alt.texto.trim())
    if (hasEmptyAlternative) {
      alert('Todas as alternativas devem ter texto!')
      return
    }

    try {
      if (editingQuestao) {
        const updatedQuestao = await questoesService.update(editingQuestao.id, {
          enunciado: formData.enunciado,
          teste_id: formData.teste_id || null,
          tri_a: formData.tri_a ? parseFloat(formData.tri_a) : null,
          tri_b: formData.tri_b ? parseFloat(formData.tri_b) : null,
          tri_c: formData.tri_c ? parseFloat(formData.tri_c) : null,
          updated_at: new Date().toISOString()
        })

        const oldAlternativas = await alternativasService.getByQuestao(editingQuestao.id)
        await Promise.all(oldAlternativas.map(alt => alternativasService.delete(alt.id)))

        const newAlternativas = await alternativasService.createMultiple(
          formData.alternativas.map(alt => ({
            questao_id: editingQuestao.id,
            texto: alt.texto,
            correta: alt.correta
          }))
        )

        setQuestoes(prev => prev.map(q => q.id === editingQuestao.id ? {
          ...updatedQuestao,
          alternativas: newAlternativas as Alternativa[]
        } as Questao : q))

      } else {
        const newQuestao = await questoesService.create({
          enunciado: formData.enunciado,
          teste_id: formData.teste_id || null,
          tri_a: formData.tri_a ? parseFloat(formData.tri_a) : null,
          tri_b: formData.tri_b ? parseFloat(formData.tri_b) : null,
          tri_c: formData.tri_c ? parseFloat(formData.tri_c) : null,
          ordem: questoes.length + 1,
          created_by: profile?.id || null
        })

        const newAlternativas = await alternativasService.createMultiple(
          formData.alternativas.map(alt => ({
            questao_id: newQuestao.id,
            texto: alt.texto,
            correta: alt.correta
          }))
        )

        setQuestoes(prev => [{
          ...newQuestao,
          alternativas: newAlternativas as Alternativa[]
        } as Questao, ...prev])
      }

      resetForm()
      setShowModal(false)
    } catch (error) {
      console.error('Erro ao salvar questão:', error)
      alert('Erro ao salvar questão. Tente novamente.')
    }
  }

  const handleEdit = (questao: Questao) => {
    setEditingQuestao(questao)
    setFormData({
      enunciado: questao.enunciado,
      teste_id: questao.teste_id || '',
      tri_a: questao.tri_a?.toString() || '1',
      tri_b: questao.tri_b?.toString() || '0',
      tri_c: questao.tri_c?.toString() || '0.2',
      alternativas: questao.alternativas.length > 0 ? questao.alternativas.map(alt => ({
        texto: alt.texto,
        correta: alt.correta
      })) : [
        { texto: '', correta: false },
        { texto: '', correta: false },
        { texto: '', correta: false },
        { texto: '', correta: false }
      ]
    })
    setShowModal(true)
  }

  const handleDelete = async (questaoId: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta questão?')) {
      try {
        await questoesService.delete(questaoId)
        setQuestoes(prev => prev.filter(q => q.id !== questaoId))
      } catch (error) {
        console.error('Erro ao excluir questão:', error)
        alert('Erro ao excluir questão.')
      }
    }
  }

  const resetForm = () => {
    setFormData({
      enunciado: '',
      teste_id: '',
      tri_a: '1',
      tri_b: '0',
      tri_c: '0.2',
      alternativas: [
        { texto: '', correta: false },
        { texto: '', correta: false },
        { texto: '', correta: false },
        { texto: '', correta: false }
      ]
    })
    setEditingQuestao(null)
  }

  const updateAlternativa = (index: number, field: 'texto' | 'correta', value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      alternativas: prev.alternativas.map((alt, i) =>
        i === index ? { ...alt, [field]: value } : alt
      )
    }))
  }

  const getTesteTitle = (testeId: string | null): string => {
    if (!testeId) return 'Sem teste'
    const teste = testes.find(t => t.id === testeId)
    return teste?.title || 'Teste não encontrado'
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs />

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestão de Questões</h1>
          <p className="text-gray-600">Gerencie questões e alternativas dos testes</p>
        </div>
        {(profile?.role === 'admin' || profile?.role === 'professor') && (
          <button
            onClick={() => {
              resetForm()
              setShowModal(true)
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            Nova Questão
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar questões..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <select
          value={selectedTeste}
          onChange={(e) => setSelectedTeste(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Selecione um teste</option>
          {testes.map(teste => (
            <option key={teste.id} value={teste.id}>{teste.title}</option>
          ))}
        </select>
      </div>

      {/* Questions List */}
      {filteredQuestoes.length === 0 ? (
        <EmptyState
          variant={searchTerm ? 'no-results' : selectedTeste ? 'no-data' : 'no-data'}
          title={
            !selectedTeste
              ? 'Selecione um teste'
              : searchTerm
                ? 'Nenhuma questão encontrada'
                : 'Nenhuma questão cadastrada'
          }
          description={
            !selectedTeste
              ? 'Escolha um teste acima para visualizar suas questões'
              : searchTerm
                ? 'Tente ajustar o termo de busca'
                : 'Comece adicionando a primeira questão para este teste'
          }
          action={selectedTeste && !searchTerm ? {
            label: 'Adicionar Questão',
            onClick: () => setShowModal(true)
          } : undefined}
        />
      ) : (
        <div className="space-y-4">
          {filteredQuestoes.map((questao) => (
            <div key={questao.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <QuestionMarkCircleIcon className="h-5 w-5 text-blue-600" />
                    <span className="text-sm text-gray-500">
                      {getTesteTitle(questao.teste_id)} • Ordem: {questao.ordem}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {questao.enunciado}
                  </h3>
                </div>

                {(profile?.role === 'admin' || profile?.role === 'professor') && (
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(questao)}
                      className="text-blue-600 hover:text-blue-800"
                      title="Editar"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(questao.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Excluir"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {questao.alternativas.map((alternativa, index) => (
                  <div
                    key={alternativa.id}
                    className={`p-3 rounded-lg border-2 flex items-center gap-3 ${alternativa.correta
                      ? 'border-green-200 bg-green-50'
                      : 'border-gray-200 bg-gray-50'
                      }`}
                  >
                    {alternativa.correta ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <XCircleIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="font-medium text-gray-700">
                      {String.fromCharCode(65 + index)})
                    </span>
                    <span className="text-gray-900">{alternativa.texto}</span>
                  </div>
                ))}
              </div>

              {(questao.tri_a || questao.tri_b || questao.tri_c) && (
                <div className="text-sm text-gray-600 border-t pt-3">
                  <span className="font-medium">Parâmetros TRI:</span>
                  {questao.tri_a && <span className="ml-2">a: {questao.tri_a}</span>}
                  {questao.tri_b && <span className="ml-2">b: {questao.tri_b}</span>}
                  {questao.tri_c && <span className="ml-2">c: {questao.tri_c}</span>}
                </div>
              )}

              <div className="text-sm text-gray-500 mt-3">
                Criada em: {formatDate(questao.created_at)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal for Add/Edit Question */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold mb-4">
              {editingQuestao ? 'Editar Questão' : 'Nova Questão'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teste
                </label>
                <select
                  value={formData.teste_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, teste_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecione um teste (opcional)</option>
                  {testes.map(teste => (
                    <option key={teste.id} value={teste.id}>{teste.title}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enunciado *
                </label>
                <textarea
                  required
                  rows={3}
                  value={formData.enunciado}
                  onChange={(e) => setFormData(prev => ({ ...prev, enunciado: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Digite o enunciado da questão..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Alternativas *
                </label>
                <div className="space-y-3">
                  {formData.alternativas.map((alternativa, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                      <span className="font-medium text-gray-700 w-8">
                        {String.fromCharCode(65 + index)})
                      </span>
                      <input
                        type="text"
                        required
                        value={alternativa.texto}
                        onChange={(e) => updateAlternativa(index, 'texto', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={`Alternativa ${String.fromCharCode(65 + index)}`}
                      />
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={alternativa.correta}
                          onChange={(e) => updateAlternativa(index, 'correta', e.target.checked)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-600">Correta</span>
                      </label>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  * Marque pelo menos uma alternativa como correta
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discriminação (a)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.tri_a}
                    onChange={(e) => setFormData(prev => ({ ...prev, tri_a: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dificuldade (b)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.tri_b}
                    onChange={(e) => setFormData(prev => ({ ...prev, tri_b: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Acerto ao Acaso (c)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.tri_c}
                    onChange={(e) => setFormData(prev => ({ ...prev, tri_c: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingQuestao ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default QuestoesPage